// _internals.ts — Helpers HTTP privés du client API.
//
// S6 (mai 2026) : éclatement de api.ts (~670 LOC) en 1 fichier par ressource.
// Ce module concentre :
//   - le wrapper fetch (`req`)
//   - le wrapper upload multipart (`upload`)
//   - les helpers de download Blob (`downloadBlob`, `downloadBlobWithFilename`,
//     `triggerBlobDownload`)
//   - le helper UX `withButton`
//   - les types HTTP (`HttpMethod`, `ActionMutationOptions`)
//
// Tous les modules api/<resource>.ts importent depuis ici plutôt que de
// dupliquer le wrapper fetch.

declare global {
  interface WindowEventMap {
    'auth:expired': Event;
    'workspace:invalidate': CustomEvent<{ entity: string }>;
  }
  interface Window {
    API_BASE?: string;
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// `notify === false` ajoute `?notify=false` au query string pour le split
// button "Enregistrer / Enregistrer sans notifier" (mai 2026). Default true
// côté backend → notif émise.
export interface ActionMutationOptions {
  notify?: boolean;
}

function handleUnauthorized(path: string): void {
  if (path && path.startsWith('/auth/')) return;
  try {
    window.dispatchEvent(new Event('auth:expired'));
  } catch (e) {
    // api/_internals.ts est chargé AVANT modules/core.ts (qui définit
    // safeWarn). On utilise console.warn direct.
    console.warn('[api] dispatchEvent auth:expired', e);
  }
}

// Entité métier déduite du chemin appelé. Point de passage UNIQUE de
// l'invalidation des tuiles du Workspace : demander à chaque écran d'émettre
// son événement garantirait qu'un l'oublie, et une tuile périmée sans message
// d'erreur est un défaut invisible. Le client d'API est le seul endroit qui
// voit passer toutes les écritures.
function invalidatedEntity(path: string): string | null {
  if (path.startsWith('/actions')) return 'action';
  if (path.startsWith('/meeting-guides')) return 'meeting_guide';
  if (path.startsWith('/meetings-records') || path.startsWith('/meeting')) return 'meeting';
  return null;
}

function notifyWorkspace(method: HttpMethod, path: string): void {
  if (method === 'GET') return;
  const entity = invalidatedEntity(path);
  if (!entity) return;
  try {
    window.dispatchEvent(new CustomEvent('workspace:invalidate', { detail: { entity } }));
  } catch (e) {
    console.warn('[api] dispatch workspace:invalidate', e);
  }
}

export type HttpError = Error & { status?: number; detail?: unknown };

// Seul endroit qui lit un corps d'erreur HTTP — pour `req` comme pour les
// quatre helpers de téléchargement / envoi. Avant le 2026-09-13 (audit dette,
// D4a) ce bloc existait en cinq copies, dont quatre avec un `d.detail || msg`
// qui mettait un objet dans le message quand `detail` était structuré ; et
// c'est ici, une fois, que le traitement du 403 (registre A4-I1) se posera.
//   - `detail` chaîne → message ;
//   - `detail` objet avec `message` (HTTPException FastAPI avec dict) → le
//     champ `message`, l'objet complet exposé via `err.detail` ;
//   - corps absent ou non-JSON → `statusText`.
async function httpError(res: Response, path: string): Promise<HttpError> {
  let msg = res.statusText;
  let detail: unknown;
  try {
    const d = (await res.json()) as { detail?: unknown };
    detail = d.detail;
    if (typeof d.detail === 'string') {
      msg = d.detail;
    } else if (
      d.detail &&
      typeof d.detail === 'object' &&
      typeof (d.detail as { message?: unknown }).message === 'string'
    ) {
      msg = (d.detail as { message: string }).message;
    }
  } catch (e) {
    console.warn('[api] error body not JSON', path, e);
  }
  const err = new Error(msg) as HttpError;
  err.status = res.status;
  err.detail = detail;
  return err;
}

// Nom de fichier annoncé par le serveur (Content-Disposition), sinon le repli.
function filenameFrom(res: Response, fallbackName: string): string {
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  return m && m[1] ? m[1] : fallbackName;
}

export async function req<T = unknown>(
  method: HttpMethod,
  path: string,
  body?: unknown,
): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch('/api/v1' + path, opts);
  if (res.status === 401) handleUnauthorized(path);
  if (!res.ok) throw await httpError(res, path);
  notifyWorkspace(method, path);
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export async function upload<T = unknown>(path: string, formData: FormData): Promise<T> {
  const res = await fetch('/api/v1' + path, {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
  });
  if (res.status === 401) handleUnauthorized(path);
  if (!res.ok) throw await httpError(res, path);
  return res.json() as Promise<T>;
}

// POST /api/v1/<path> avec body JSON → renvoie un Blob (utilisé pour les
// exports XLSX/PPTX/CSV qui produisent un fichier binaire). Centralise
// 401/erreur réseau au lieu de fetch direct dans les modules.
export async function downloadBlob(path: string, body: unknown): Promise<Blob> {
  const res = await fetch('/api/v1' + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (res.status === 401) handleUnauthorized(path);
  if (!res.ok) throw await httpError(res, path);
  return res.blob();
}

// Helper pratique : déclenche le téléchargement navigateur d'un Blob
// (créé par downloadBlob) avec le nom de fichier souhaité.
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// POST + récupère un Blob ET le filename depuis Content-Disposition dans
// le même appel. Pour les endpoints qui choisissent dynamiquement le nom
// de fichier côté serveur (ex. /meeting-support/finalize qui suffixe la
// date + format choisi). Centralisé ici (audit P1 #8) au lieu de fetch
// direct dans les modules — garantit la gestion 401.
export async function downloadBlobWithFilename(
  path: string,
  body: unknown,
  fallbackName: string,
): Promise<string> {
  const res = await fetch('/api/v1' + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (res.status === 401) handleUnauthorized(path);
  if (!res.ok) throw await httpError(res, path);
  const filename = filenameFrom(res, fallbackName);
  triggerBlobDownload(await res.blob(), filename);
  return filename;
}

// GET → Blob téléchargé, avec le filename du Content-Disposition. Le pendant
// en lecture de `downloadBlobWithFilename` : certains exports N'ONT PAS de
// corps de requête (le serveur régénère le fichier à partir de ce qu'il a
// enregistré), et forcer un POST pour un GET rendrait l'URL non partageable.
// Renvoie aussi ce que le serveur a retiré de l'export, quand il le dit — un
// corps binaire n'a pas d'autre endroit où le porter qu'un en-tête.
export async function downloadGetBlob(
  path: string,
  fallbackName: string,
): Promise<{ filename: string; removed: number }> {
  const res = await fetch('/api/v1' + path, { credentials: 'same-origin' });
  if (res.status === 401) handleUnauthorized(path);
  if (!res.ok) throw await httpError(res, path);
  const filename = filenameFrom(res, fallbackName);
  triggerBlobDownload(await res.blob(), filename);
  return { filename, removed: Number(res.headers.get('X-Ornexia-Support-Removed') || 0) };
}

// POST multipart FormData → reçoit un Blob + filename depuis Content-Disposition.
// Utilisé pour generate-cr (envoi optionnel d'un fichier support, retour DOCX).
export async function uploadBlobWithFilename(
  path: string,
  formData: FormData,
  fallbackName: string,
): Promise<string> {
  const res = await fetch('/api/v1' + path, {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
  });
  if (res.status === 401) handleUnauthorized(path);
  if (!res.ok) throw await httpError(res, path);
  const filename = filenameFrom(res, fallbackName);
  triggerBlobDownload(await res.blob(), filename);
  return filename;
}

// Helper UX : désactive un bouton pendant l'exécution d'une promesse.
// Empêche les double-clics qui créeraient des doublons en backend, et
// donne un feedback visuel (label "..." + curseur attente). Restaure
// l'état initial à la fin (succès ou échec).
export async function withButton<T>(
  btn: HTMLButtonElement | null | undefined,
  fn: () => Promise<T>,
): Promise<T | undefined> {
  if (!btn || btn.disabled) return undefined;
  const prevLabel = btn.textContent;
  const prevDisabled = btn.disabled;
  btn.disabled = true;
  btn.dataset['loading'] = 'true';
  btn.textContent = '…';
  try {
    return await fn();
  } finally {
    btn.disabled = prevDisabled;
    delete btn.dataset['loading'];
    btn.textContent = prevLabel;
  }
}
